import { useQuery } from '@tanstack/react-query'
import { proceduresApi } from '@/api/proceduresApi'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import type { ProcedureAssignee } from '@/types/cgt'

/** Mock roster so the Assign To pickers stay populated offline. */
const MOCK_USERS: ProcedureAssignee[] = [
  { id: 'mock-paul', name: 'Paul P.' },
  { id: 'mock-aaron', name: 'Aaron A.' },
  { id: 'mock-maria', name: 'Maria L.' },
  { id: 'mock-devon', name: 'Devon S.' },
]

/**
 * Roster for the procedure/form Assign To pickers. Pass `enabled: false` for
 * users without procedures.assign — the backend route 403s them, so the query
 * must never fire.
 */
export function useProcedureAssignableUsers(
  enabled: boolean,
  /**
   * The run being assigned. When given, the roster is the people who can
   * PERFORM that run's procedure. Part of the query key, so two runs on
   * different procedures do not share a cached roster.
   */
  procedureId?: string,
): {
  users: ProcedureAssignee[]
  isLoading: boolean
} {
  const { selectedTenantId } = useTenant()

  const query = useQuery({
    queryKey: ['procedure-assignable-users', selectedTenantId, procedureId ?? null] as const,
    enabled: enabled && !useMockApi && selectedTenantId !== null,
    queryFn: () => proceduresApi.listAssignableUsers(procedureId),
  })

  return {
    users: useMockApi ? MOCK_USERS : (query.data ?? []),
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
  }
}
